/**
 * Iframe step-set resolver.
 *
 * Every embedded product mirrors its site counterpart field-for-field:
 * /iframe/v1/debt asks exactly what /debt asks. That holds by construction
 * rather than by maintenance — the steps are read straight from the site's own
 * STEPS_PER_PRODUCT and transformed, so there is no per-product iframe file to
 * keep in sync and a change to a site product reaches the iframe with it.
 *
 * Two things are applied on top of the site steps:
 *
 *   1. The stateless transforms (iframe-derive.js) — session-backed field types
 *      and step hooks the cross-domain iframe cannot run.
 *   2. The feature filter — optional step groups (Flinks, account creation) the
 *      server switched off. See app/Helpers/IframeFeatures.php.
 *
 * `version` is the forward-compatibility seam: the iframe URL is a permanent
 * contract, so if a version ever needs to diverge, branch on it here and point
 * that version at a forked source set. Today every version resolves the same
 * way, which is why nothing reads it yet.
 */

import { getSteps } from '@/form/helpers/steps-per-product.js';
import { deriveIframeSteps } from '@/form/products/iframe-derive.js';

const DEFAULT_PRODUCT = 'personal_loan';
const DEFAULT_COUNTRY = 'CA';

/**
 * Drop steps whose feature group is switched off.
 *
 * A step is only removed when its feature is *explicitly* false. An absent or
 * unknown flag keeps the step: the default is the full site form, and a missing
 * flag is not an instruction to shorten it. Untagged steps are always kept —
 * they are the form's mandatory spine, phone verification included.
 *
 * @param {Array} steps
 * @param {Object<string, boolean>} features
 * @returns {Array}
 */
export function filterStepsByFeatures(steps, features = {}) {
    if (!Array.isArray(steps)) {
        return [];
    }
    if (!features || typeof features !== 'object') {
        return steps;
    }

    return steps.filter((step) => {
        const feature = step && step.iframeFeature;
        return !feature || features[feature] !== false;
    });
}

/**
 * The iframe step set for a product.
 *
 * getSteps() already falls back to the country's personal_loan (and then CA's)
 * for anything unknown, so an unrecognised product renders a working form
 * rather than an empty one — the server's allowlist is what rejects a product
 * outright, before the page is ever served.
 *
 * @param {Object} options
 * @param {string} [options.product]
 * @param {string} [options.country]
 * @param {string} [options.version]  reserved — see the file comment
 * @param {Object<string, boolean>} [options.features]
 * @returns {Array} step definitions
 */
export function resolveIframeSteps({ product, country, features } = {}) {
    const siteSteps = getSteps(product || DEFAULT_PRODUCT, country || DEFAULT_COUNTRY);

    return filterStepsByFeatures(deriveIframeSteps(siteSteps), features);
}

export default resolveIframeSteps;
